import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Fetch backorders with filters and pagination
export const getBackorders = createAsyncThunk(
  "backorder/getBackorders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.BACKORDER.SLUG}?${queryString}`,
      );
      return data;
      // const backorder_list = data.data.list;
      // const totalRecords = data.data.totalRecords;
      // return { backorder_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Get single backorder by ID
export const getBackorderById = createAsyncThunk(
  "backorder/getBackorderById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.BACKORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get backorder items
export const getBackorderItems = createAsyncThunk(
  "backorder/getBackorderItems",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.BACKORDER.GET_ITEMS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get next backorder number
export const getNextBackorderNumber = createAsyncThunk(
  "backorder/getNextBackorderNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.BACKORDER.GET_NEXT_BACKORDER_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get backorder availability for a sales order (for create page)
export const getBackorderAvailability = createAsyncThunk(
  "backorder/getBackorderAvailability",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.BACKORDER.AVAILABILITY(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get backorders by sales order
export const getBackordersBySalesOrder = createAsyncThunk(
  "backorder/getBackordersBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.BACKORDER.GET_BY_SALES_ORDER(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Create new backorder
export const createBackorder = createAsyncThunk(
  "backorder/createBackorder",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.BACKORDER.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Update backorder item
export const updateBackorderItem = createAsyncThunk(
  "backorder/updateBackorderItem",
  async (
    { backorderId, itemId, payload },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const { data } = await axios.put(
        API_URL.BACKORDER.UPDATE_ITEM(backorderId, itemId),
        payload,
      );
      // Refresh detail if viewing the same backorder
      const detailId = getState().backorder.backorder_detail?.backorderId;
      if (detailId === backorderId) {
        dispatch(getBackorderById(backorderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Close backorder
export const closeBackorder = createAsyncThunk(
  "backorder/closeBackorder",
  async ({ backorderId, payload }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(
        API_URL.BACKORDER.CLOSE(backorderId),
        payload,
      );
      // Refresh detail if viewing the same backorder
      const detailId = getState().backorder.backorder_detail?.backorderId;
      if (detailId === backorderId) {
        dispatch(getBackorderById(backorderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get fulfill items for a sales order (for fulfill backorders screen)
export const getFulfillItems = createAsyncThunk(
  "backorder/getFulfillItems",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.BACKORDER.FULFILL_ITEMS}?salesOrderId=${salesOrderId}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Create picklist from backorder items
export const createPicklistFromBackorder = createAsyncThunk(
  "backorder/createPicklistFromBackorder",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(
        API_URL.BACKORDER.CREATE_PICKLIST,
        payload,
      );
      // Refresh fulfill items after creating picklist
      if (payload.salesOrderId) {
        dispatch(getFulfillItems(payload.salesOrderId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Check if backorders for a sales order require attention
// Attention is required when acknowledged backorder qty is insufficient
// (e.g., stock was consumed by other orders)
export const checkBackorderAttention = createAsyncThunk(
  "backorder/checkBackorderAttention",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.BACKORDER.ATTENTION_CHECK(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ============================================
// Multi-View Aggregated Thunks
// ============================================

// Get backorders by SKU (Item-wise view)
export const getBackordersBySku = createAsyncThunk(
  "backorder/getBackordersBySku",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayloadSku(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.BACKORDER.BY_SKU}?${queryString}`,
      );
      return data;
      // return { list: data.data.list, totalRecords: data.data.totalRecords };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Get SKU detail (all backorders for an item)
export const getBackorderSkuDetail = createAsyncThunk(
  "backorder/getBackorderSkuDetail",
  async ({ itemId, filter = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filter.Statuses) {
        filter.Statuses.forEach((s) => params.append("Statuses", s));
      }
      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.BACKORDER.BY_SKU}/${itemId}?${queryString}`
        : `${API_URL.BACKORDER.BY_SKU}/${itemId}`;
      const { data } = await axios.get(url);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get backorders by Customer
export const getBackordersByCustomer = createAsyncThunk(
  "backorder/getBackordersByCustomer",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayloadCustomer(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.BACKORDER.BY_CUSTOMER}?${queryString}`,
      );
      return data;
      // return { list: data.data.list, totalRecords: data.data.totalRecords };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Get Customer detail (all backorders for a customer)
export const getBackorderCustomerDetail = createAsyncThunk(
  "backorder/getBackorderCustomerDetail",
  async ({ customerId, filter = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filter.Statuses) {
        filter.Statuses.forEach((s) => params.append("Statuses", s));
      }
      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.BACKORDER.BY_CUSTOMER}/${customerId}?${queryString}`
        : `${API_URL.BACKORDER.BY_CUSTOMER}/${customerId}`;
      const { data } = await axios.get(url);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get backorders grouped by Sales Order
export const getBackordersGroupedBySalesOrder = createAsyncThunk(
  "backorder/getBackordersGroupedBySalesOrder",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayloadSalesOrder(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.BACKORDER.GROUPED_BY_SALES_ORDER}?${queryString}`,
      );
      return data;
      // return { list: data.data.list, totalRecords: data.data.totalRecords };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  backorder_list: [],
  backorder_detail: null,
  backorder_items: [],
  backorder_availability: null,
  backorders_by_sales_order: [],
  fulfill_items: null,
  current_values: null,
  active_payload: null,
  total_records: 0,
  next_backorder_number: null,
  // Attention check state
  attention_check: null,
  attention_check_loading: false,
  // Multi-view state
  backorder_by_sku_list: [],
  backorder_sku_detail: null,
  backorder_by_customer_list: [],
  backorder_customer_detail: null,
  backorder_by_sales_order_list: [],
  active_payload_sku: null,
  active_payload_customer: null,
  active_payload_sales_order: null,
};

const backorderSlice = createSlice({
  name: "backorder",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
    },
    resetBackorderDetail(state) {
      state.backorder_detail = null;
      state.backorder_items = [];
    },
    resetBackorderAvailability(state) {
      state.backorder_availability = null;
    },
    resetFulfillItems(state) {
      state.fulfill_items = null;
    },
    // Multi-view reducers
    setActivePayloadSku(state, action) {
      state.active_payload_sku = action.payload;
    },
    setActivePayloadCustomer(state, action) {
      state.active_payload_customer = action.payload;
    },
    setActivePayloadSalesOrder(state, action) {
      state.active_payload_sales_order = action.payload;
    },
    resetSkuDetail(state) {
      state.backorder_sku_detail = null;
    },
    resetCustomerDetail(state) {
      state.backorder_customer_detail = null;
    },
    resetAttentionCheck(state) {
      state.attention_check = null;
      state.attention_check_loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBackorders.fulfilled, (state, action) => {
        state.backorder_list = action.payload.data.list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getBackorderById.fulfilled, (state, action) => {
        state.backorder_detail = action.payload;
      })
      .addCase(getBackorderItems.fulfilled, (state, action) => {
        state.backorder_items = action.payload;
      })
      .addCase(getNextBackorderNumber.fulfilled, (state, action) => {
        state.next_backorder_number = action.payload;
      })
      .addCase(getBackorderAvailability.fulfilled, (state, action) => {
        state.backorder_availability = action.payload;
      })
      .addCase(getBackordersBySalesOrder.fulfilled, (state, action) => {
        state.backorders_by_sales_order = action.payload;
      })
      .addCase(getFulfillItems.fulfilled, (state, action) => {
        state.fulfill_items = action.payload;
      })
      // Attention check reducers
      .addCase(checkBackorderAttention.pending, (state) => {
        state.attention_check_loading = true;
      })
      .addCase(checkBackorderAttention.fulfilled, (state, action) => {
        state.attention_check = action.payload;
        state.attention_check_loading = false;
      })
      .addCase(checkBackorderAttention.rejected, (state) => {
        state.attention_check_loading = false;
      })
      // Multi-view reducers
      .addCase(getBackordersBySku.fulfilled, (state, action) => {
        state.backorder_by_sku_list = action.payload.data.list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getBackorderSkuDetail.fulfilled, (state, action) => {
        state.backorder_sku_detail = action.payload;
      })
      .addCase(getBackordersByCustomer.fulfilled, (state, action) => {
        state.backorder_by_customer_list = action.payload.data.list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getBackorderCustomerDetail.fulfilled, (state, action) => {
        state.backorder_customer_detail = action.payload;
      })
      .addCase(getBackordersGroupedBySalesOrder.fulfilled, (state, action) => {
        state.backorder_by_sales_order_list = action.payload.data.list;
        state.total_records = action.payload.data.totalRecords || 0;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetBackorderDetail,
  resetBackorderAvailability,
  resetFulfillItems,
  resetAttentionCheck,
  // Multi-view actions
  setActivePayloadSku,
  setActivePayloadCustomer,
  setActivePayloadSalesOrder,
  resetSkuDetail,
  resetCustomerDetail,
} = backorderSlice.actions;

export default backorderSlice.reducer;
